import * as angular from 'angular';
import {IPromise, IQService} from 'angular';
import * as _ from "lodash";
import {ViewSettingsService} from "./view-settings-service.service";
import {
  ViewSettingsHolderType,
  ViewSettingsModel,
  ViewSettingsType
} from "../models/view-settings.model";

/*
 * Per-user package details columns, shared by service-items-table and fulfillment-line-packages-table.
 *
 * settings shape: { [columnKey: string]: boolean }
 *   columnKey - a fixed column key (e.g. 'packsInItem', 'sku', 'legs') or a package custom field id.
 *   value     - true = shown, false = hidden.
 *
 * Keys missing from the map fall back to each table's defaults.
 * Tables show different column sets, so every save is merged into the stored map.
 * Loaded once per session.
 */
export type PackageDetailsColumnsMap = Record<string, boolean>;

const PAGE_NAME = 'packageDetails';

export class PackageDetailsViewSettingsService {

  private viewTableSettings: ViewSettingsModel;
  private loadPromise: IPromise<void>;

  /* @ngInject */
  constructor(private viewSettingsService: ViewSettingsService, private $q: IQService) {
  }

  load(): IPromise<PackageDetailsColumnsMap> {
    if (!this.loadPromise) {
      this.loadPromise = this.viewSettingsService.getByUser(PAGE_NAME, ViewSettingsType.ViewTable)
        .then((viewTableSettings: ViewSettingsModel[]) => {
          const userViewTableSettings = viewTableSettings.find(v => v.holderType === ViewSettingsHolderType.User);

          if (userViewTableSettings) {
            this.viewTableSettings = userViewTableSettings;
          } else {
            this.viewTableSettings = viewTableSettings.find(v => v.holderType === ViewSettingsHolderType.Site);
          }
        })
        .catch((error) => {
          this.loadPromise = null;
          return this.$q.reject(error);
        });
    }

    return this.loadPromise.then(() => this.viewTableSettings?.settings || {});
  }

  save(columnsMap: PackageDetailsColumnsMap) {
    const settings = {...this.viewTableSettings?.settings, ...columnsMap};

    if (this.viewTableSettings) {
      const viewTableSettingsCopy = {...this.viewTableSettings, settings: settings};
      viewTableSettingsCopy.holderType = ViewSettingsHolderType.User;

      this.viewSettingsService.updateByUser(this.viewTableSettings.id, viewTableSettingsCopy)
        .then((viewSettingsTable: ViewSettingsModel) => {
          this.viewTableSettings = viewSettingsTable
        })

    } else {
      const viewTableSettings = {
        pageName: PAGE_NAME,
        holderType: ViewSettingsHolderType.User,
        settingsType: ViewSettingsType.ViewTable,
        settings: settings
      }
      this.viewSettingsService.createByUser(viewTableSettings)
        .then((viewSettingsTable: ViewSettingsModel) => {
          this.viewTableSettings = viewSettingsTable
        })
    }
  }

  toColumnsMap(columns, packageDynamicColumns?): PackageDetailsColumnsMap {
    return {
      ..._.mapValues(columns, 'enabled'),
      ..._.mapValues(_.keyBy(packageDynamicColumns, 'id'), 'enabled')
    };
  }

  applyColumnsMap(columnsMap: PackageDetailsColumnsMap, columns, packageDynamicColumns?) {
    return {
      columns: _.mapValues(columns, (column, key) =>
        _.has(columnsMap, key) ? {...column, enabled: columnsMap[key]} : column),
      packageDynamicColumns: _.map(packageDynamicColumns, (column) =>
        _.has(columnsMap, column.id) ? {...column, enabled: columnsMap[column.id]} : column)
    };
  }
}

angular
  .module('bringoz.admin.commons')
  .service('packageDetailsViewSettingsService', PackageDetailsViewSettingsService);
